/**
 * Passungs-Ampel einer Ausschreibung. Der Server liefert sie nur, wenn ein Prozessschritt des
 * Mandanten die Ausschreibung gegen seine Kriterien beurteilt hat (tri-server
 * docs/tender-fit-score.md); ohne Urteil gibt es keine Ampel, auch keine graue.
 *
 * - In der Zeile steht nur der Punkt mit der Stufe in Worten — die Zahl nicht.
 * - Beim Überfahren (oder Klick/Tastatur) öffnet ein Popover: Passung in Prozent, bis zu drei
 *   Gründe, und — wo `onFeedback` gesetzt ist — „Passt“ / „Passt nicht“.
 * - Hat eine Person geantwortet, folgt die **Anzeige** ihr („Passt nicht“ grau mit Personen-Symbol,
 *   „Passt“ grün), wie Filter und Sortierung auf dem Server. Die Ampel der Kriterien bleibt
 *   gespeichert und steht im Popover — der Unterschied ist „Ampel widersprochen“.
 */
import { useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Loader2, Minus, Plus, ThumbsDown, ThumbsUp, UserCheck, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { TenderFit, TenderFitFeedback } from "../../TenderSearchProfileAPI";

type Verdict = TenderFitFeedback["verdict"] | null;

export const FIT_DOT_CLASS: Record<TenderFit["level"], string> = {
  green: "bg-emerald-600",
  yellow: "bg-amber-400",
  red: "bg-red-600",
};

export const fitPercent = (score: number): string => `${Math.round(score * 100)} %`;

/** „Passt gut“ / „Passt teilweise“ / „Passt kaum“ — the level in words, without the number. */
export const fitLevelLabel = (fit: TenderFit, t: TFunction): string => t(`tenderBoard.fit.level.${fit.level}`);

/** What the row shows: the person's verdict when there is one, else the criteria's level. */
export type FitShown = TenderFit["level"] | "personFits" | "personNotFits";

export const fitShown = (fit: TenderFit, feedback: TenderFitFeedback | null | undefined): FitShown =>
  feedback?.verdict === "fits" ? "personFits" : feedback?.verdict === "notFits" ? "personNotFits" : fit.level;

/** Everything the popover says, for the trigger's accessible name and the tests. */
export const fitSummaryLines = (fit: TenderFit, t: TFunction): string[] => [
  t("tenderBoard.fit.score", { value: fitPercent(fit.score) }),
  ...fit.reasons.map((reason) => reason.text),
];

/** Wait this long before closing, so the pointer can travel from the dot into the popover. */
const CLOSE_DELAY_MS = 150;

export function FitLight({
  fit,
  feedback = null,
  onFeedback,
  people = {},
}: {
  fit: TenderFit | null | undefined;
  feedback?: TenderFitFeedback | null;
  /** Answer „Passt“ / „Passt nicht“, or null to undo. Rejects with the server's message. */
  onFeedback?: (verdict: Verdict) => Promise<unknown>;
  /** E-mail → name, for „bewertet von …“. */
  people?: Record<string, string>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };
  useEffect(() => cancelClose, []);

  // Same as the Bestandskunden badge: inside the side panel the sheet takes Escape away from Radix.
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [open]);

  if (!fit) return null;

  const criteriaLabel = fitLevelLabel(fit, t);
  const shown = fitShown(fit, feedback);
  const overruled = shown === "personFits" || shown === "personNotFits";
  const label = overruled ? t(`tenderBoard.fit.shown.${shown}`) : criteriaLabel;
  const lines = fitSummaryLines(fit, t);
  const by = feedback?.by ? (people[feedback.by] ?? feedback.by.replace(/@.*$/, "")) : null;

  const answer = async (verdict: Verdict) => {
    if (!onFeedback) return;
    setBusy(true);
    setError(null);
    try {
      await onFeedback(verdict);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-level={fit.level}
          data-shown={shown}
          aria-label={`${t("tenderBoard.fit.label")}: ${label}${
            overruled ? ` · ${t("tenderBoard.fit.criteria", { level: criteriaLabel })}` : ""
          } · ${lines.join(" · ")}`}
          className="inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-px text-[11px] font-medium text-foreground/80 hover:bg-muted"
          onMouseEnter={() => {
            cancelClose();
            setOpen(true);
          }}
          onMouseLeave={scheduleClose}
          onClick={(event) => event.stopPropagation()}
        >
          {shown === "personNotFits" ? (
            <UserX size={11} aria-hidden className="text-muted-foreground" />
          ) : shown === "personFits" ? (
            <UserCheck size={11} aria-hidden className="text-emerald-700" />
          ) : (
            <span aria-hidden className={`inline-block h-2 w-2 rounded-full ${FIT_DOT_CLASS[fit.level]}`} />
          )}
          <span className={shown === "personNotFits" ? "text-muted-foreground line-through decoration-1" : undefined}>
            {label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 space-y-3 text-sm"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <span aria-hidden className={`inline-block h-2.5 w-2.5 rounded-full ${FIT_DOT_CLASS[fit.level]}`} />
          <span className="font-semibold">
            {overruled ? t("tenderBoard.fit.criteria", { level: criteriaLabel }) : criteriaLabel}
          </span>
          <span className="ml-auto tabular-nums text-muted-foreground">{lines[0]}</span>
        </div>
        {fit.reasons.length > 0 && (
          <ul className="space-y-1">
            {fit.reasons.map((reason) => (
              <li key={reason.text} className="flex gap-1.5 text-xs leading-snug">
                {reason.effect === "negative" ? (
                  <Minus size={12} aria-hidden className="mt-0.5 shrink-0 text-red-700" />
                ) : reason.effect === "positive" ? (
                  <Plus size={12} aria-hidden className="mt-0.5 shrink-0 text-emerald-700" />
                ) : (
                  <span aria-hidden className="mt-0.5 w-3 shrink-0 text-center text-muted-foreground">
                    ·
                  </span>
                )}
                <span>{reason.text}</span>
              </li>
            ))}
          </ul>
        )}
        {onFeedback && (
          <div className="space-y-2 border-t border-border pt-2">
            {feedback ? (
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>
                  {t(feedback.verdict === "fits" ? "tenderBoard.fit.ratedFits" : "tenderBoard.fit.ratedNotFits")}
                  {by ? ` · ${t("tenderBoard.fit.ratedBy", { name: by })}` : ""}
                </span>
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                  disabled={busy}
                  onClick={() => answer(null)}
                >
                  {t("tenderBoard.fit.undo")}
                </button>
              </div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground">{t("tenderBoard.fit.question")}</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="gap-1" disabled={busy} onClick={() => answer("fits")}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <ThumbsUp size={12} aria-hidden />}
                    {t("tenderBoard.fit.fits")}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1" disabled={busy} onClick={() => answer("notFits")}>
                    <ThumbsDown size={12} aria-hidden />
                    {t("tenderBoard.fit.notFits")}
                  </Button>
                </div>
              </>
            )}
            {error && <div className="text-xs text-red-700">{error}</div>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
